import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, ExternalLink, Filter, Loader2, Search, Share2, Sparkles, Star, X, ZoomIn } from "lucide-react";
import EnterpriseShell from "../components/public/EnterpriseShell";
import { api, API_BASE, formatErr, resolveMediaUrl } from "../lib/api";
import { applySeoTags } from "../lib/seo";
import { useWebsiteContent, whatsappUrl } from "../lib/websiteContent";
import { toast } from "sonner";

function productImage(product) {
  return resolveMediaUrl(product?.main_image) || "/banner.png";
}

function inquiryMessage(product) {
  const lines = [
    "Hello RP Infotech, I am interested in this product.",
    `Product: ${product?.product_name || "Product"}`,
  ];
  if (product?.model_number) lines.push(`Model: ${product.model_number}`);
  if (product?.sku) lines.push(`SKU: ${product.sku}`);
  if (typeof window !== "undefined") lines.push(`Product page: ${window.location.origin}/e-catalogue/${product?.slug || ""}`);
  return lines.join("\n");
}

function inquiryUrl(product, settings) {
  return whatsappUrl(
    { ...settings, whatsapp: product?.whatsapp_number || settings?.whatsapp },
    inquiryMessage(product)
  );
}

export function ECataloguePage() {
  const { data: website } = useWebsiteContent();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [availableFilters, setAvailableFilters] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [brand, setBrand] = useState("all");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    applySeoTags({
      pageKey: "e-catalogue",
      metaTitle: "E-Catalogue | RP Infotech Product Catalogue",
      metaDescription: "Browse RP Infotech digital signage software, LED TVs, LED active displays, LED interactive panels and business automation products with specifications, brochures and WhatsApp inquiry.",
      canonicalUrl: `${window.location.origin}/e-catalogue`,
      ogTitle: "RP Infotech E-Catalogue",
      ogDescription: "Premium product catalogue for digital signage, LED displays and automation solutions.",
    });
  }, []);

  useEffect(() => {
    api.get("/e-catalogue/categories").then(({ data }) => setCategories(data || [])).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!query.trim()) {
        setSuggestions([]);
        return;
      }
      api.get("/e-catalogue/autocomplete", { params: { q: query } }).then(({ data }) => setSuggestions(data || [])).catch(() => setSuggestions([]));
    }, 220);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let mounted = true;
    api.get("/e-catalogue/filters", { params: { q: query, category, brand } })
      .then(({ data }) => {
        if (!mounted) return;
        const nextFilters = Array.isArray(data) ? data : [];
        const allowed = new Set(nextFilters.map((item) => item.key));
        setAvailableFilters(nextFilters);
        setFilters((current) => Object.fromEntries(Object.entries(current).filter(([key]) => allowed.has(key))));
      })
      .catch(() => mounted && setAvailableFilters([]));
    return () => { mounted = false; };
  }, [query, category, brand]);

  const params = useMemo(() => {
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
    return { q: query, category, brand, page, limit: 12, ...activeFilters };
  }, [query, category, brand, filters, page]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get("/e-catalogue", { params })
      .then(({ data }) => {
        if (!mounted) return;
        setItems((current) => (page === 1 ? data.items || [] : [...current, ...(data.items || [])]));
        setBrands(data.brands || []);
        setHasMore(Boolean(data.has_more));
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [params, page]);

  const resetAnd = (fn) => {
    setPage(1);
    fn();
  };

  return (
    <EnterpriseShell content={website}>
      <section className="bg-[#07142f] text-white">
        <div className="mx-auto grid max-w-[1400px] gap-6 px-4 py-10 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.22em] text-orange-400">RP Infotech</div>
            <h1 className="mt-3 text-4xl font-black tracking-tight lg:text-5xl">E-Catalogue</h1>
            <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-200">Explore digital signage software, LED TVs, LED active displays, LED interactive panels and automation products with live specifications and dynamic brochures.</p>
            <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
              <a href={whatsappUrl(website.settings, "Hello RP Infotech, I viewed the E-Catalogue and want product pricing with demo.")} className="inline-flex min-h-[52px] items-center justify-center rounded-sm bg-green-600 px-5 py-3 font-black text-white">
                WhatsApp Product Pricing
              </a>
              <a href="#catalogue-products" className="inline-flex min-h-[52px] items-center justify-center rounded-sm border border-white/25 px-5 py-3 font-black text-white">
                Browse Products
              </a>
            </div>
          </div>
          <div className="grid gap-3 rounded-sm border border-white/10 bg-white/10 p-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => resetAnd(() => setQuery(e.target.value))}
                placeholder="Search by product, model, SKU, barcode, product code..."
                className="h-[52px] w-full rounded-sm border border-white/20 bg-white px-12 py-4 font-semibold text-slate-950 outline-none"
              />
              {query && <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" onClick={() => resetAnd(() => setQuery(""))} aria-label="Clear search"><X className="h-5 w-5" /></button>}
              {suggestions.length > 0 && (
                <div className="absolute inset-x-0 top-[calc(100%+0.25rem)] z-20 overflow-hidden rounded-sm border border-slate-200 bg-white text-slate-900 shadow-xl">
                  {suggestions.map((item) => (
                    <Link key={item.slug} to={`/e-catalogue/${item.slug}`} className="block px-4 py-3 text-sm font-bold hover:bg-slate-50">
                      {item.product_name} <span className="font-normal text-slate-500">{item.model_number || item.sku}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <select value={category} onChange={(e) => resetAnd(() => { setCategory(e.target.value); setFilters({}); })} className="rounded-sm border border-white/20 bg-white px-4 py-3 font-bold text-slate-950">
                <option value="all">All categories</option>
                {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
              <select value={brand} onChange={(e) => resetAnd(() => { setBrand(e.target.value); setFilters({}); })} className="rounded-sm border border-white/20 bg-white px-4 py-3 font-bold text-slate-950">
                <option value="all">All brands</option>
                {brands.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </div>
          </div>
        </div>
      </section>

      <section id="catalogue-products" className="mx-auto grid max-w-[1400px] gap-6 px-4 py-8 lg:grid-cols-[280px_1fr] lg:px-8">
        <aside className="h-max border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.16em] text-slate-500"><Filter className="h-4 w-4" /> Filters</div>
            {Object.values(filters).some(Boolean) && (
              <button onClick={() => resetAnd(() => setFilters({}))} className="text-xs font-black uppercase tracking-[0.12em] text-blue-700">Clear</button>
            )}
          </div>
          <div className="mt-4 grid gap-3">
            {availableFilters.length === 0 ? (
              <div className="border border-dashed border-slate-200 bg-slate-50 p-3 text-sm font-semibold text-slate-500">Choose a category to see product-specific filters.</div>
            ) : availableFilters.map((filter) => (
              <label key={filter.key} className="grid gap-1 text-sm font-bold text-slate-700">
                {filter.label}
                <select
                  value={filters[filter.key] || ""}
                  onChange={(e) => resetAnd(() => setFilters((current) => {
                    const next = { ...current };
                    if (e.target.value) next[filter.key] = e.target.value;
                    else delete next[filter.key];
                    return next;
                  }))}
                  className="rounded-sm border border-slate-200 px-3 py-2 font-semibold outline-none focus:border-blue-500"
                >
                  <option value="">Any {filter.label.toLowerCase()}</option>
                  {(filter.options || []).map((option) => (
                    <option key={`${filter.key}-${option.value}`} value={option.value}>{option.value}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </aside>

        <div>
          {loading && page === 1 ? (
            <div className="flex min-h-80 items-center justify-center text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading catalogue...</div>
          ) : items.length === 0 ? (
            <div className="border border-slate-200 bg-slate-50 p-10 text-center">
              <h2 className="text-2xl font-black">No products found</h2>
              <p className="mt-2 text-slate-500">Try a different keyword or remove a filter.</p>
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {items.map((product) => <CatalogueCard key={product.id} product={product} settings={website.settings} />)}
            </div>
          )}
          {hasMore && (
            <div className="mt-8 text-center">
              <button onClick={() => setPage((value) => value + 1)} disabled={loading} className="rounded-sm bg-slate-950 px-6 py-3 font-black text-white">
                {loading ? "Loading..." : "Load More Products"}
              </button>
            </div>
          )}
        </div>
      </section>
    </EnterpriseShell>
  );
}

function CatalogueCard({ product, settings }) {
  const navigate = useNavigate();
  const detailPath = `/e-catalogue/${product.slug}`;
  const goToDetail = () => navigate(detailPath);
  return (
    <article
      role="link"
      tabIndex={0}
      onClick={goToDetail}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          goToDetail();
        }
      }}
      className="group flex h-full cursor-pointer flex-col overflow-hidden border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative aspect-[4/3] bg-slate-100">
        <img src={productImage(product)} alt={product.image_alt_text || product.product_name} className="h-full w-full object-contain p-4" loading="lazy" />
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {product.featured && <span className="rounded-sm bg-blue-600 px-2 py-1 text-xs font-black text-white"><Star className="mr-1 inline h-3 w-3" /> Featured</span>}
          {product.new_arrival && <span className="rounded-sm bg-orange-500 px-2 py-1 text-xs font-black text-white"><Sparkles className="mr-1 inline h-3 w-3" /> New</span>}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="text-xs font-black uppercase tracking-[0.16em] text-blue-700">{product.category || "Product"}</div>
        <h2 className="mt-2 text-xl font-black tracking-tight text-slate-950">{product.product_name}</h2>
        <div className="mt-1 text-sm font-semibold text-slate-500">{product.model_number || product.product_code || product.sku}</div>
        <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{product.short_description}</p>
        <div className="mt-4 grid gap-2 text-xs">
          {(product.key_specifications || []).slice(0, 4).map((spec) => (
            <div key={`${product.id}-${spec.key}`} className="flex justify-between gap-3 border-b border-slate-100 pb-2">
              <span className="font-bold text-slate-500">{spec.key}</span>
              <span className="text-right font-black text-slate-800">{spec.value}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto grid gap-2 pt-5 sm:grid-cols-2">
          <Link to={detailPath} onClick={(event) => event.stopPropagation()} className="flex min-h-12 items-center justify-center rounded-sm bg-slate-950 px-4 py-3 text-center text-sm font-black text-white">View Details</Link>
          <a href={inquiryUrl(product, settings)} onClick={(event) => event.stopPropagation()} target="_blank" rel="noreferrer" className="flex min-h-12 items-center justify-center rounded-sm border border-green-600 px-4 py-3 text-center text-sm font-black text-green-700">
            WhatsApp
          </a>
        </div>
      </div>
    </article>
  );
}

export function ECatalogueDetailPage() {
  const { slug } = useParams();
  const { data: website } = useWebsiteContent();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brochureLoading, setBrochureLoading] = useState(false);
  const [activeImage, setActiveImage] = useState("");
  const [zoomImage, setZoomImage] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/e-catalogue/${slug}`)
      .then(({ data }) => {
        if (!mounted) return;
        setProduct(data);
        setActiveImage(data.main_image || "");
        applySeoTags(data.seo || {});
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [slug]);

  const gallery = useMemo(() => {
    if (!product) return [];
    return [product.main_image, ...(product.gallery_images || [])].filter(Boolean);
  }, [product]);

  if (loading) {
    return <EnterpriseShell content={website}><div className="flex min-h-[60vh] items-center justify-center text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading product...</div></EnterpriseShell>;
  }
  if (!product) {
    return <EnterpriseShell content={website}><div className="mx-auto max-w-[1000px] px-4 py-16"><h1 className="text-3xl font-black">Product not found</h1></div></EnterpriseShell>;
  }

  const downloadBase = `${API_BASE}/e-catalogue/${encodeURIComponent(product.slug)}/brochure`;
  const brochureFilename = `RP-Signage-${product.slug || "product"}-brochure.pdf`;

  const downloadBrochure = async () => {
    if (!product?.slug || brochureLoading) return;
    setBrochureLoading(true);
    try {
      const response = await fetch(`${downloadBase}?format=pdf`, {
        method: "GET",
        credentials: "omit",
        headers: { Accept: "application/pdf" },
      });
      if (!response.ok) {
        let message = "Could not generate PDF brochure.";
        try {
          const payload = await response.json();
          message = payload?.detail || payload?.message || message;
        } catch {
          message = await response.text() || message;
        }
        throw new Error(message);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = brochureFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Brochure downloaded. Send WhatsApp inquiry for pricing and availability.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBrochureLoading(false);
    }
  };

  return (
    <EnterpriseShell content={website}>
      <section className="mx-auto grid max-w-[1400px] gap-8 px-4 py-8 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
        <div>
          <div className="relative aspect-[4/3] border border-slate-200 bg-slate-50">
            <img src={resolveMediaUrl(activeImage) || productImage(product)} alt={product.image_alt_text || product.product_name} className="h-full w-full object-contain p-5" />
            <button onClick={() => setZoomImage(activeImage || product.main_image)} className="absolute bottom-4 right-4 rounded-sm bg-white px-3 py-2 text-sm font-black shadow"><ZoomIn className="mr-1 inline h-4 w-4" /> Zoom</button>
          </div>
          <div className="mt-3 grid grid-cols-5 gap-2">
            {gallery.map((image) => (
              <button key={image} onClick={() => setActiveImage(image)} className={`aspect-square border bg-white p-1 ${activeImage === image ? "border-blue-600" : "border-slate-200"}`}>
                <img src={resolveMediaUrl(image)} alt="" className="h-full w-full object-contain" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">{product.category}</div>
          <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 lg:text-5xl">{product.product_name}</h1>
          <p className="mt-4 text-lg leading-8 text-slate-600">{product.short_description}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Info label="Model Number" value={product.model_number} />
            <Info label="Product Code" value={product.product_code} />
            <Info label="SKU" value={product.sku} />
            <Info label="Brand" value={product.brand} />
            <Info label="Warranty" value={product.warranty} />
            <Info label="Sub Category" value={product.sub_category} />
          </div>
          <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
            <a href={inquiryUrl(product, website.settings)} target="_blank" rel="noreferrer" className="inline-flex min-h-[52px] items-center justify-center rounded-sm bg-green-600 px-5 py-3 font-black text-white">
              WhatsApp Inquiry
            </a>
            <button onClick={downloadBrochure} disabled={brochureLoading} className="inline-flex min-h-[52px] items-center justify-center rounded-sm bg-slate-950 px-5 py-3 font-black text-white disabled:cursor-wait disabled:opacity-70">
              {brochureLoading ? <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> : <Download className="mr-2 inline h-4 w-4" />}
              {brochureLoading ? "Downloading..." : "PDF Brochure"}
            </button>
            <button onClick={() => navigator.share?.({ title: product.product_name, url: window.location.href }) || navigator.clipboard?.writeText(window.location.href)} className="inline-flex min-h-[52px] items-center justify-center rounded-sm border border-slate-200 px-5 py-3 font-black"><Share2 className="mr-2 inline h-4 w-4" /> Share</button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-8 lg:px-8">
        <div className="grid gap-4 border border-green-200 bg-green-50 p-5 sm:grid-cols-[1fr_auto] sm:items-center">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.16em] text-green-700">Need price or stock confirmation?</div>
            <h2 className="mt-2 text-2xl font-black text-slate-950">Send this product to RP Infotech on WhatsApp.</h2>
            <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">We can share pricing, installation options, compatible signage player details and demo timing.</p>
          </div>
          <a href={inquiryUrl(product, website.settings)} target="_blank" rel="noreferrer" className="inline-flex min-h-[52px] items-center justify-center rounded-sm bg-green-600 px-5 py-3 font-black text-white">
            Continue on WhatsApp
          </a>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-8 px-4 pb-10 lg:grid-cols-[1fr_320px] lg:px-8">
        <div className="grid gap-6">
          <DetailSection title="Full Description"><p className="text-slate-700 leading-8">{product.full_description || product.short_description}</p></DetailSection>
          <DetailSection title="Features"><BulletList items={product.features} /></DetailSection>
          {product.product_video && (
            <DetailSection title="Product Video">
              <video src={resolveMediaUrl(product.product_video)} controls playsInline className="aspect-video w-full border border-slate-100 bg-black object-contain" />
            </DetailSection>
          )}
          <DetailSection title="Technical Specifications">
            <div className="grid gap-2 md:grid-cols-2">
              {(product.specifications || []).map((spec) => <Info key={`${spec.key}-${spec.value}`} label={spec.key} value={spec.value} />)}
            </div>
          </DetailSection>
          <DetailSection title="Downloads">
            <div className="flex flex-wrap gap-3">
              <button onClick={downloadBrochure} disabled={brochureLoading} className="min-h-12 rounded-sm border border-slate-200 px-4 py-3 font-black disabled:cursor-wait disabled:opacity-70">{brochureLoading ? "Downloading PDF..." : "PDF Brochure"}</button>
              <a href={`${downloadBase}?format=pdf&print_ready=true`} className="inline-flex min-h-12 items-center rounded-sm border border-slate-200 px-4 py-3 font-black">Print Ready PDF</a>
              <a href={`${downloadBase}?format=jpg`} className="inline-flex min-h-12 items-center rounded-sm border border-slate-200 px-4 py-3 font-black">JPG</a>
              <a href={`${downloadBase}?format=png`} className="inline-flex min-h-12 items-center rounded-sm border border-slate-200 px-4 py-3 font-black">PNG</a>
            </div>
          </DetailSection>
          {product.related_products?.length > 0 && (
            <DetailSection title="Related Products">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {product.related_products.map((item) => <CatalogueCard key={item.id} product={item} settings={website.settings} />)}
              </div>
            </DetailSection>
          )}
        </div>

        <aside className="h-max border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-black">QR Code</h2>
          <p className="mt-2 text-sm text-slate-500">Scan to open this live product page.</p>
          <img src={`${API_BASE}/e-catalogue/${encodeURIComponent(product.slug)}/qr`} alt={`QR code for ${product.product_name}`} className="mt-4 aspect-square w-full border border-slate-100 object-contain p-4" />
          <a href={product.detail_url} className="mt-4 flex items-center gap-2 text-sm font-black text-blue-700"><ExternalLink className="h-4 w-4" /> Open product link</a>
        </aside>
      </section>

      {zoomImage && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" onClick={() => setZoomImage("")}>
          <button className="absolute right-4 top-4 text-white" aria-label="Close zoom"><X className="h-7 w-7" /></button>
          <img src={resolveMediaUrl(zoomImage)} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-2 border-t border-slate-200 bg-white/95 p-3 shadow-2xl backdrop-blur md:hidden">
        <a href={inquiryUrl(product, website.settings)} target="_blank" rel="noreferrer" className="flex min-h-12 items-center justify-center rounded-sm bg-green-600 px-3 text-sm font-black text-white">WhatsApp Price</a>
        <button onClick={downloadBrochure} disabled={brochureLoading} className="flex min-h-12 items-center justify-center rounded-sm bg-slate-950 px-3 text-sm font-black text-white disabled:opacity-70">
          {brochureLoading ? "Downloading..." : "PDF Brochure"}
        </button>
      </div>
    </EnterpriseShell>
  );
}

function Info({ label, value }) {
  return <div className="border border-slate-200 bg-white p-3"><div className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">{label}</div><div className="mt-1 font-black text-slate-950">{value || "-"}</div></div>;
}

function DetailSection({ title, children }) {
  return <section className="border border-slate-200 bg-white p-6 shadow-sm"><h2 className="text-2xl font-black tracking-tight text-slate-950">{title}</h2><div className="mt-4">{children}</div></section>;
}

function BulletList({ items = [] }) {
  if (!items.length) return <p className="text-slate-500">Not specified.</p>;
  return <div className="grid gap-3 md:grid-cols-2">{items.map((item) => <div key={item} className="border border-slate-100 bg-slate-50 p-3 font-semibold text-slate-700">{item}</div>)}</div>;
}
